import React, { useEffect, useState } from "react";
import { X, Trash2, Plus, Minus, ShoppingCart, Loader2 } from "lucide-react";
import { brand } from "../mock/content";
import { useCart, cartStore } from "../lib/cart";
import { CheckoutFunnel } from "./CheckoutFunnel";

const API = process.env.REACT_APP_BACKEND_URL;
const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const CartDrawer = ({ open, onClose }) => {
  const items = useCart();
  const [provinces, setProvinces] = useState([]);
  const [zone, setZone] = useState("");
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pickup, setPickup] = useState(false);
  const [showFunnel, setShowFunnel] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/shop/provinces`).then((r) => r.json()).then((d) => setProvinces(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const itemsKey = JSON.stringify(items.map((c) => [c.slug, c.qty]));

  useEffect(() => {
    if (!open) return undefined;
    const list = cartStore.items();
    if (list.length === 0) { setQuote(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/api/shop/cart/quote`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: list.map((c) => ({ slug: c.slug, qty: c.qty })), zone: pickup ? null : (zone || null), pickup }),
    }).then((r) => r.json()).then((d) => { if (!cancelled) setQuote(d); })
      .catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itemsKey, zone, pickup]);

  const count = items.reduce((s, x) => s + x.qty, 0);

  return (
    <>
      <div data-testid="cart-drawer" className={`fixed inset-0 z-[90] overflow-hidden transition-opacity ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
        <div className="absolute inset-0 bg-black/70" onClick={onClose} />
        <div className={`absolute right-0 top-0 h-full w-full max-w-md bg-[#0b0b0b] border-l border-white/15 flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.18em]" style={{ color: brand.yellow, fontFamily: "Space Grotesk" }}>
              <ShoppingCart size={16} /> Carrello ({count})
            </div>
            <button data-testid="cart-close" onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {items.length === 0 && <p className="text-white/60 text-sm py-8 text-center">Il carrello è vuoto.</p>}
            {items.map((it) => (
              <div key={it.slug} data-testid={`cart-item-${it.slug}`} className="flex items-start gap-3 border-b border-white/10 pb-4">
                <div className="flex-1">
                  <p className="text-white/85 text-sm">{it.model || it.name}</p>
                  <p className="text-white/60 text-xs">{eur(it.price_net)} cad.</p>
                  <div className="flex items-center border border-white/40 w-fit mt-2 rounded">
                    <button data-testid={`cart-minus-${it.slug}`} onClick={() => cartStore.setQty(it.slug, it.qty - 1)} className="px-2.5 py-1.5 text-white/80 hover:bg-white/10"><Minus size={15} /></button>
                    <span className="w-9 text-center text-sm font-semibold text-white">{it.qty}</span>
                    <button data-testid={`cart-plus-${it.slug}`} onClick={() => cartStore.setQty(it.slug, it.qty + 1)} className="px-2.5 py-1.5 text-white/80 hover:bg-white/10"><Plus size={15} /></button>
                  </div>
                </div>
                <button data-testid={`cart-remove-${it.slug}`} onClick={() => cartStore.remove(it.slug)} className="text-white/60 hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>

          {items.length > 0 && (
            <div className="border-t border-white/10 px-5 py-4 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="cart-delivery-shipping" onClick={() => setPickup(false)} className={`px-3 py-2 text-sm border transition-colors ${!pickup ? "border-white bg-white/10 text-white" : "border-white/20 text-white/60"}`}>Spedizione</button>
                <button data-testid="cart-delivery-pickup" onClick={() => setPickup(true)} className={`px-3 py-2 text-sm border transition-colors ${pickup ? "border-white bg-white/10 text-white" : "border-white/20 text-white/60"}`}>Ritiro · Gratis</button>
              </div>
              {!pickup && (
                <div>
                  <span className="block text-white/50 text-[11px] uppercase tracking-[0.1em] mb-1">Provincia di consegna</span>
                  <select data-testid="cart-zone" value={zone} onChange={(e) => setZone(e.target.value)} className="w-full bg-transparent border border-white/20 px-3 py-2 text-sm text-white outline-none">
                    <option value="" className="bg-black">— seleziona —</option>
                    {provinces.map((p) => <option key={p.sigla} value={p.sigla} className="bg-black">{p.name} ({p.sigla})</option>)}
                  </select>
                </div>
              )}
              {quote && (
                <div className="text-sm space-y-1">
                  <div className="flex justify-between text-white/60"><span>Prodotti</span><span>{eur(quote.prezzo_netto_totale)}</span></div>
                  <div className="flex justify-between text-white/60"><span>{pickup ? "Ritiro in fabbrica" : `Trasporto${zone ? ` — ${zone}` : ""}`}</span><span>{pickup ? "Gratis" : (quote.trasporto != null ? eur(quote.trasporto) : "—")}</span></div>
                  <div className="flex justify-between text-white/60"><span>IVA {(quote.iva_rate * 100).toFixed(0)}%</span><span>{eur(quote.iva)}</span></div>
                  <div className="flex justify-between items-center pt-2 border-t border-white/10"><span className="text-white/70">TOTALE</span><span data-testid="cart-total" className="text-xl font-black" style={{ color: brand.yellow, fontFamily: "Archivo" }}>{eur(quote.totale)}</span></div>
                </div>
              )}
              <button data-testid="cart-checkout-btn" onClick={() => { if (!pickup && !zone) return; setShowFunnel(true); }} disabled={(!pickup && !zone) || loading}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-black text-sm uppercase tracking-[0.14em] font-semibold transition-transform hover:scale-[1.02] disabled:opacity-40"
                style={{ background: brand.yellow, fontFamily: "Space Grotesk" }}>
                {loading ? <Loader2 size={16} className="animate-spin" /> : <ShoppingCart size={16} />} Procedi all'acquisto
              </button>
              {!pickup && !zone && <p className="text-white/60 text-xs text-center">Seleziona la provincia per calcolare il trasporto.</p>}
            </div>
          )}
        </div>
      </div>

      <CheckoutFunnel
        open={showFunnel}
        onClose={() => setShowFunnel(false)}
        zone={zone}
        pickup={pickup}
        quote={quote}
        cartItems={items}
        onSuccessClearCart={() => cartStore.clear()}
      />
    </>
  );
};
